import React, { useState, useEffect } from 'react';
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Brain, 
  Clock, 
  Shield, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Lock,
  History,
  Target,
  Lightbulb,
  ChevronRight,
  Eye,
  BarChart3
} from 'lucide-react';
import { API_BASE } from '@/App';

// ============================================================================
// CLARIAS VOCABULARY - Consistent Language Lock
// ============================================================================
const CLARIAS_VOCABULARY = {
  SAFE: { label: 'SAFE', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  BORDERLINE: { label: 'BORDERLINE', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  UNSAFE: { label: 'UNSAFE', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30' }
};

// ============================================================================
// VERDICT TIMELINE COMPONENT
// ============================================================================
const VerdictTimeline = ({ timeline, distribution }) => {
  if (!timeline || timeline.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Complete tests to build your verdict timeline.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Distribution Summary */}
      <div className="flex gap-2 justify-center mb-4">
        {Object.entries(distribution || {}).map(([verdict, count]) => (
          <Badge 
            key={verdict}
            className={`${CLARIAS_VOCABULARY[verdict]?.bg} ${CLARIAS_VOCABULARY[verdict]?.color} border ${CLARIAS_VOCABULARY[verdict]?.border}`}
          >
            {verdict}: {count}
          </Badge>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
        
        {timeline.map((entry, idx) => {
          const style = CLARIAS_VOCABULARY[entry.verdict] || CLARIAS_VOCABULARY.BORDERLINE;
          return (
            <div key={idx} className="relative pl-10 pb-6">
              <div className={`absolute left-2 w-5 h-5 rounded-full ${style.bg} border-2 ${style.border} flex items-center justify-center`}>
                {entry.verdict === 'SAFE' && <Shield className="w-3 h-3 text-emerald-500" />}
                {entry.verdict === 'BORDERLINE' && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                {entry.verdict === 'UNSAFE' && <AlertTriangle className="w-3 h-3 text-red-500" />}
              </div>
              
              <div className={`p-3 rounded-lg ${style.bg} border ${style.border}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <span className={`font-bold ${style.color}`}>{entry.verdict}</span>
                    <p className="text-sm text-slate-300 mt-1">{entry.reason}</p>
                  </div>
                  <span className="text-xs text-slate-500">{entry.month}</span>
                </div>
                {entry.test_count > 1 && (
                  <span className="text-xs text-slate-500 mt-1 block">
                    Based on {entry.test_count} tests
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-center text-slate-500 italic mt-4">
        This timeline reflects how your preparation actually evolved.
      </p>
    </div>
  );
};

// ============================================================================
// RISK PROFILE COMPONENT
// ============================================================================
const RiskProfileCard = ({ profile }) => {
  if (!profile || !profile.profile_ready) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>{profile?.message || "Complete more tests to generate your risk profile."}</p>
        {profile?.tests_needed && (
          <p className="text-sm mt-2">Tests needed: {profile.tests_needed}</p>
        )}
      </div>
    );
  }

  const stabilityColors = {
    improving: 'text-emerald-500',
    stable: 'text-blue-500',
    volatile: 'text-amber-500',
    deteriorating: 'text-red-500',
    new: 'text-slate-400',
    inactive: 'text-slate-500'
  };

  return (
    <div className="space-y-4">
      {/* Primary Risk Identity */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-indigo-500/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <Brain className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Your Risk Identity</p>
            <h3 className="text-xl font-bold text-white">{profile.primary_risk}</h3>
          </div>
        </div>
        
        {profile.secondary_risks && profile.secondary_risks.length > 0 && (
          <div className="flex gap-2 mt-3">
            {profile.secondary_risks.map((risk, idx) => (
              <Badge key={idx} variant="outline" className="text-slate-300 border-slate-600">
                {risk}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Stability Indicator */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <p className="text-xs text-slate-400 mb-1">Stability</p>
          <p className={`font-semibold capitalize ${stabilityColors[profile.stability] || 'text-slate-300'}`}>
            {profile.stability === 'improving' && <TrendingUp className="inline w-4 h-4 mr-1" />}
            {profile.stability === 'deteriorating' && <TrendingDown className="inline w-4 h-4 mr-1" />}
            {profile.stability}
          </p>
        </div>
        
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <p className="text-xs text-slate-400 mb-1">Confidence Gap</p>
          <p className={`font-semibold capitalize ${
            profile.confidence_accuracy_gap === 'high' ? 'text-red-400' :
            profile.confidence_accuracy_gap === 'moderate' ? 'text-amber-400' : 'text-emerald-400'
          }`}>
            {profile.confidence_accuracy_gap}
          </p>
        </div>
      </div>

      {/* Data Points */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-1">
        <span>Based on {profile.data_points} observations</span>
        <span>Updated: {new Date(profile.last_updated).toLocaleDateString()}</span>
      </div>

      <p className="text-xs text-center text-slate-500 italic">
        This profile reflects your behaviour, not your potential.
      </p>
    </div>
  );
};

// ============================================================================
// BEHAVIOURAL MEMORY COMPONENT
// ============================================================================
const BehaviouralMemory = ({ memory }) => {
  if (!memory || !memory.activated || memory.total_observations === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Complete more tests to build behavioural memory.</p>
      </div>
    );
  }

  const patternIcons = {
    over_attempting: AlertTriangle,
    panic_signal: AlertTriangle,
    under_attempting: TrendingDown,
    accuracy_confidence_mismatch: BarChart3,
    negative_mark_accumulation: TrendingDown
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="p-3 rounded-lg bg-indigo-900/30 border border-indigo-500/30">
        <div className="flex items-center justify-between">
          <span className="text-indigo-300 text-sm">{memory.memory_depth}</span>
          <Badge className="bg-indigo-500/20 text-indigo-300">
            {memory.total_observations} observations
          </Badge>
        </div>
      </div>

      {/* Dominant Patterns */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-slate-300">Dominant Patterns</h4>
        {memory.dominant_patterns?.map((pattern, idx) => {
          const Icon = patternIcons[pattern.pattern?.toLowerCase().replace(/ /g, '_')] || Brain;
          const severityColor = pattern.severity_breakdown?.severe > 0 ? 'border-red-500/30' : 
                               pattern.severity_breakdown?.moderate > 0 ? 'border-amber-500/30' : 'border-slate-700';
          
          return (
            <div key={idx} className={`p-3 rounded-lg bg-slate-800/50 border ${severityColor}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-400" />
                  <span className="text-white font-medium">{pattern.pattern}</span>
                </div>
                <span className="text-slate-400 text-sm">{pattern.occurrences}x</span>
              </div>
              
              {pattern.severity_breakdown && (
                <div className="flex gap-2 mt-2">
                  {pattern.severity_breakdown.severe > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 text-xs">
                      {pattern.severity_breakdown.severe} severe
                    </Badge>
                  )}
                  {pattern.severity_breakdown.moderate > 0 && (
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                      {pattern.severity_breakdown.moderate} moderate
                    </Badge>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-center text-slate-500 italic">
        {memory.microcopy}
      </p>
    </div>
  );
};

// ============================================================================
// PERSONALIZED INSIGHT COMPONENT
// ============================================================================
const PersonalizedInsight = ({ insight }) => {
  if (!insight || !insight.activated || !insight.insight) {
    return null;
  }

  const typeStyles = {
    warning: { bg: 'bg-amber-900/30', border: 'border-amber-500/30', icon: AlertTriangle, iconColor: 'text-amber-500' },
    critical: { bg: 'bg-red-900/30', border: 'border-red-500/30', icon: AlertTriangle, iconColor: 'text-red-500' },
    encouragement: { bg: 'bg-emerald-900/30', border: 'border-emerald-500/30', icon: TrendingUp, iconColor: 'text-emerald-500' },
    neutral: { bg: 'bg-indigo-900/30', border: 'border-indigo-500/30', icon: Lightbulb, iconColor: 'text-indigo-500' }
  };

  const style = typeStyles[insight.type] || typeStyles.neutral;
  const Icon = style.icon;

  return (
    <div className={`p-4 rounded-xl ${style.bg} border ${style.border}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${style.iconColor}`} />
        </div>
        <div>
          <p className="text-white font-medium">{insight.insight}</p>
          <p className="text-xs text-slate-400 mt-2">{insight.based_on}</p>
        </div>
      </div>
      
      {insight.primary_risk && (
        <Badge className="mt-3 bg-slate-800 text-slate-300">
          Profile: {insight.primary_risk}
        </Badge>
      )}
      
      <p className="text-xs text-slate-500 mt-3 italic">{insight.accuracy_note}</p>
    </div>
  );
};

// ============================================================================
// MAIN SWITCHING COST WALL PAGE
// ============================================================================
const SwitchingCostWallPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({
    widget: null,
    memory: null,
    timeline: null,
    profile: null,
    insight: null
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [widgetRes, memoryRes, timelineRes, profileRes, insightRes] = await Promise.all([
        managedAxios.get(`${API_BASE}/clarias/dashboard-widget`, { headers }).catch(() => ({ data: null })),
        managedAxios.get(`${API_BASE}/clarias/memory`, { headers }).catch(() => ({ data: null })),
        managedAxios.get(`${API_BASE}/clarias/timeline`, { headers }).catch(() => ({ data: null })),
        managedAxios.get(`${API_BASE}/clarias/risk-profile`, { headers }).catch(() => ({ data: null })),
        managedAxios.get(`${API_BASE}/clarias/personalized-insight`, { headers }).catch(() => ({ data: null }))
      ]);

      setData({
        widget: widgetRes.data,
        memory: memoryRes.data,
        timeline: timelineRes.data,
        profile: profileRes.data,
        insight: insightRes.data
      });
    } catch (err) {
      console.error('Failed to fetch switching cost data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-indigo-400">Loading CLARIAS insights...</div>
      </div>
    );
  }

  const isActivated = data.widget?.activated;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">CLARIAS Intelligence</h1>
              <p className="text-slate-400 text-sm">Your Preparation Memory & Risk Profile</p>
            </div>
          </div>
        </div>

        {/* Activation Progress (if not activated) */}
        {!isActivated && data.widget?.progress && (
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-amber-500" />
                <div>
                  <h3 className="font-semibold text-white">Unlock Behavioural Insights</h3>
                  <p className="text-sm text-slate-400">Complete more tests to activate CLARIAS intelligence</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 rounded-lg bg-slate-800 border border-slate-700">
                  <p className="text-2xl font-bold text-white">{data.widget.progress.gs_tests}/3</p>
                  <p className="text-xs text-slate-400">GS Tests</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-slate-800 border border-slate-700">
                  <p className="text-2xl font-bold text-white">{data.widget.progress.csat_tests}/2</p>
                  <p className="text-xs text-slate-400">CSAT Tests</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-slate-800 border border-slate-700">
                  <p className="text-2xl font-bold text-white">{data.widget.progress.verdicts_received}</p>
                  <p className="text-xs text-slate-400">Verdicts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personalized Insight (if available) */}
        {data.insight?.activated && data.insight?.insight && (
          <div className="mb-6">
            <PersonalizedInsight insight={data.insight} />
          </div>
        )}

        {/* Main Tabs */}
        {isActivated && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid grid-cols-3 bg-slate-800 border border-slate-700">
              <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-600">
                Overview
              </TabsTrigger>
              <TabsTrigger value="timeline" className="data-[state=active]:bg-indigo-600">
                Timeline
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-indigo-600">
                Risk Profile
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Quick Summary */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-400" />
                      Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-2 rounded bg-slate-800">
                      <span className="text-slate-300">Observations</span>
                      <span className="font-bold text-white">{data.widget?.summary?.observations || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-slate-800">
                      <span className="text-slate-300">Verdicts</span>
                      <span className="font-bold text-white">{data.widget?.summary?.verdicts || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-slate-800">
                      <span className="text-slate-300">Latest Verdict</span>
                      {data.widget?.summary?.latest_verdict && (
                        <Badge className={`${CLARIAS_VOCABULARY[data.widget.summary.latest_verdict]?.bg} ${CLARIAS_VOCABULARY[data.widget.summary.latest_verdict]?.color}`}>
                          {data.widget.summary.latest_verdict}
                        </Badge>
                      )}
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-slate-800">
                      <span className="text-slate-300">Risk Identity</span>
                      <span className="font-medium text-indigo-400 text-sm">{data.widget?.summary?.risk_identity || 'Analyzing...'}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Behavioural Memory */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-400" />
                      Behavioural Memory
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Longitudinal pattern analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BehaviouralMemory memory={data.memory} />
                  </CardContent>
                </Card>
              </div>

              {/* Value Statement */}
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/30 text-center">
                <p className="text-indigo-300 font-medium">{data.widget?.value_statement}</p>
                <p className="text-xs text-slate-500 mt-2">{data.widget?.transparency_note}</p>
              </div>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-emerald-400" />
                    Verdict History Timeline
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Your preparation journey in verdicts
                  </CardDescription>
                  {data.timeline?.current_trend && (
                    <Badge className={`mt-2 ${
                      data.timeline.current_trend === 'improving' ? 'bg-emerald-500/20 text-emerald-400' :
                      data.timeline.current_trend === 'deteriorating' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      Trend: {data.timeline.current_trend}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <VerdictTimeline 
                    timeline={data.timeline?.timeline} 
                    distribution={data.timeline?.verdict_distribution}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Risk Profile Tab */}
            <TabsContent value="profile">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-400" />
                    Personal Risk Profile
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Your unique preparation identity
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RiskProfileCard profile={data.profile} />
                </CardContent>
              </Card>

              {/* Data Moat Notice */}
              <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700 text-center">
                <Lock className="w-4 h-4 text-slate-500 mx-auto mb-2" />
                <p className="text-xs text-slate-500">
                  Behavioural insights are generated uniquely within CLARIAS and cannot be exported.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Identity Statement */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-xs italic">
            CLARIAS does not trap users. It earns permanence by remembering what others forget.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SwitchingCostWallPage;
